import React, { useState } from 'react';

import moment from "moment";
import { DatePicker } from 'antd';
import { toLower } from 'lodash';
import { BorderedField, Box, Flex } from '../../components/Flex';
import { Label, Paragraph } from '../../components/Text';
import { Checkbox, Input, InputFile } from '../../components/Input';
import { Select } from '../../components/Select';
import { FormTooltip } from '../../components/Tooltip';
import { TextButton, Button } from '../../components/Button';
import { CalendIcon } from '../../icons/CalendIcon';
import { ExplainIcon } from '../../icons/ExplainIcon';
import { PlusIcon } from '../../icons/PlusIcon';
import { RemoveIcon } from '../../icons/RemoveIcon';

const { Option } = Select;

const ActionFormItem = ({
    type,
    name,
    label,
    value,
    options,
    onValueChange,
    error,
    max,
    disabled,
    uppercase,
    search,
    selectWidth,
    extraLabel,
    loading,
    multiple,
    validateValue
 }) => {

    const [ state, setState ] = useState({
        visible: false,
        value: null,
        checked: true
    });

    const onChange = (value) => {
        if (multiple) {
            if (value !== state.value) {
                let checked = validateValue(name, value);
                setState(state => ({ ...state, value : value, checked: checked }));
            }
        } else {
            onValueChange(name, value);
        }
    };

    const renderField = (type, value) => {
        return (type === "file"
        ? <InputFile
            error={error}
            bordered={false}
            name="file"
            selected={multiple ? state.value : value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.files[0])}
            onCancel={() => onChange(null)}
        />
        : <BorderedField hasError={typeof error === "string"}>
            {type === "select" &&
            <Select
                allowClear
                bordered={false}
                loading={loading}
                disabled={disabled}
                dropdownMatchSelectWidth={selectWidth}
                showSearch={search}
                value={value}
                onChange={value => onChange(value)}
                filterOption={(input, option) =>
                    toLower(option.value).indexOf(toLower(input)) >= 0 ||
                    toLower(option.children).indexOf(toLower(input)) >= 0
                }
            >
                {options.map(item => (
                    <Option key={item.key}>{item.title}</Option>
                ))}
            </Select>}
            {type === "input" &&
            <Input
                bordered={false}
                value={value}
                maxLength={max}
                uppercase={uppercase}
                disabled={disabled}
                onChange={e => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
            />}
            {type === "text" &&
            <Input.TextArea
                bordered={false}
                value={value}
                disabled={disabled}
                maxLength={max}
                onChange={e => onChange(e.target.value)}
            />}
            {type === "date" &&
            <DatePicker
                allowClear={false}
                bordered={false}
                disabled={disabled}
                value={value && toLower(value) !== "none" && moment(value)}
                suffixIcon={<CalendIcon />}
                style={{ width: "100%" }}
                onChange={value => onChange(value.format("YYYY-MM-DD"))}
            />
            }
        </BorderedField>)
    };

    const onMultipleValueChange = (value) => {
        if (state.checked || value === null) {
            onValueChange(name, value);
            setState({ visible: false, value: null, checked: true });
        }
    };

    return(
        type === "checkbox"
            ? <Checkbox checked={value} onChange={e => onChange(e.target.checked)}>{label}</Checkbox>
            : multiple
                ? <>
                    {state.visible
                        ? <>
                            {renderField(type)}
                            <Flex justify="flex-end" align="center" gap={{ right: 37 }}>
                                <Button type="link" onClick={() => onMultipleValueChange(null)}>Cancel</Button>
                                <Button disabled={!state.value || !state.checked} onClick={() => onMultipleValueChange(state.value)}>
                                    Add
                                </Button>
                            </Flex>
                        </>
                        : <Box flatten error={error} background="#F0F4FB" height="54px" width="100%" align="center" justify="center" margin={{ bottom: "12px" }}>
                            <TextButton disabled={disabled} onClick={() => setState({ visible: true })}>
                                <PlusIcon style={{ marginRight: "8px" }} />
                                Add {label}
                            </TextButton>
                        </Box>}
                    {Array.isArray(value)
                        ? value.map((val, idx) => (
                            <Box key={`multiple-field-${idx}`} flatten direction="row" background="#F0F4FB" width="100%" align="center" justify="space-between" margin={{ bottom: idx === value.length - 1 ? 0 : "12px" }}>
                                <Paragraph maxWidth="206px" nowrap>{val.name || "?"}</Paragraph>
                                {!disabled &&
                                <RemoveIcon onClick={() => onValueChange(name, null, idx)} color="var(--sp-danger-color)" hoverColor="var(--sp-danger-hover-color)" />}
                            </Box>))
                        : null}
                </>
            : <Flex direction="column" flex={1}>
                <Flex justify="space-between" margin={{ bottom: "4px"}}>
                    <Flex align="center" gap={{ right: 4 }}>
                        <Label name="form-item-label-text">{label}</Label>
                        {error &&
                        <>
                            <FormTooltip text={error}>
                                <ExplainIcon />
                            </FormTooltip>
                        </>}
                    </Flex>
                    {extraLabel}
                </Flex>
                {renderField(type, value)}
            </Flex>
    );
};

export { ActionFormItem };